import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import DateSelectionButton from "../../components/DateSelectionButton";
import Loading from "../../components/Loading";
import {
  useStandoutMutations,
  useStandoutStatus,
} from "../../hooks/useCustomQuery";

// Season 0 here is TMDB's specials (standouts statuses list real seasons only —
// unlike the add-to-collection picker, where 0 stands for the whole series)
const seasonTabLabel = (seasonNumber) =>
  seasonNumber === 0 ? "Specials" : `S${seasonNumber}`;

/**
 * Runs "make this a standout" for one item, driven by `contentId` (null = idle):
 *   1. a multi-season series first asks which season, with the same scrolling
 *      S1/S2/... tabs as adding a show to a collection (minus "whole series" —
 *      standouts are season-level); a single-season series goes straight to
 *      season 1;
 *   2. an item with no finish dated this year asks for one — marking a standout
 *      says it was finished this year, so we confirm the date rather than block;
 *   3. then adds it.
 * Calls onClose when the flow ends, whether it added, was cancelled or bailed out,
 * and onAdded(contentId) first when it added (the season, for a series).
 */
export default function StandoutAddFlow({
  contentId,
  title,
  onClose,
  onAdded,
}) {
  const { data: status, isFetching } = useStandoutStatus(contentId, {
    enabled: !!contentId,
  });
  const { addStandout, removeStandout, isAdding, isRemoving } =
    useStandoutMutations();

  const [seasonPickerOpen, setSeasonPickerOpen] = useState(false);
  const [dateTarget, setDateTarget] = useState(null); // { contentId, label }
  const [finishDate, setFinishDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  // The status query refetches after every mutation; only the first fresh
  // response for a contentId should kick the flow off.
  const startedForRef = useRef(null);

  const endFlow = () => {
    setSeasonPickerOpen(false);
    setDateTarget(null);
    startedForRef.current = null;
    onClose();
  };

  // Route one concrete item (a movie, book or season) to the next step
  const handleTarget = async (target, label) => {
    if (target.needs_date) {
      setFinishDate(dayjs().format("YYYY-MM-DD"));
      setSeasonPickerOpen(false);
      setDateTarget({ contentId: target.content_id, label });
      return;
    }
    // safeMutate resolves with nothing when the add failed
    if (await addStandout({ contentId: target.content_id })) {
      onAdded?.(target.content_id);
    }
    endFlow();
  };

  useEffect(() => {
    if (!contentId || !status || isFetching) return;
    if (startedForRef.current === contentId) return;
    startedForRef.current = contentId;

    if (status.is_series) {
      if (status.seasons.length === 1) {
        handleTarget(status.seasons[0], status.title ?? title);
      } else {
        setSeasonPickerOpen(true);
      }
      return;
    }
    if (status.state === "contains") {
      toast.info(`That's already one of your ${status.year} standouts`);
      endFlow();
      return;
    }
    handleTarget(status, status.title ?? title);
    // handleTarget/endFlow are recreated each render; the ref guards re-entry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, status, isFetching, title]);

  const seriesTitle = status?.title ?? title;

  const handleSeasonSelect = async (seasonContentId) => {
    const season = status.seasons.find((s) => s.content_id === seasonContentId);
    // A ticked season is already a standout — tapping it removes it
    if (season.state === "contains") {
      setSeasonPickerOpen(false);
      await removeStandout(season.content_id);
      endFlow();
      return;
    }
    await handleTarget(
      season,
      `${seriesTitle} ${seasonTabLabel(season.season_number)}`,
    );
  };

  const handleConfirmDate = async () => {
    if (await addStandout({ contentId: dateTarget.contentId, finishDate })) {
      onAdded?.(dateTarget.contentId);
    }
    endFlow();
  };

  const year = status?.year;

  return (
    <>
      {/* Mirrors the season picker in CollectionWithVirtualisation's add dialog */}
      <Dialog
        open={seasonPickerOpen}
        onClose={endFlow}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            paddingTop: "8vh",
          },
        }}
      >
        <DialogContent
          sx={{
            border: "none",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 108,
            p: 2,
          }}
        >
          <Box>
            <Typography variant="body2">
              Select which season of <b>{seriesTitle}</b> stood out...
            </Typography>
            <Tabs
              value={false} // uncontrolled
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, seasonContentId) =>
                handleSeasonSelect(seasonContentId)
              }
              sx={{
                maxWidth: "100%",
                minHeight: 30,
                mt: 2,
              }}
            >
              {status?.seasons?.map(({ season_number, content_id, state }) => (
                <Tab
                  key={content_id}
                  value={content_id}
                  label={seasonTabLabel(season_number)}
                  disabled={isAdding || isRemoving}
                  {...(state === "contains" && {
                    icon: <CheckIcon fontSize="inherit" />,
                    iconPosition: "end",
                  })}
                  sx={{
                    minWidth: 20,
                    py: 0,
                    minHeight: 30,
                  }}
                />
              ))}
            </Tabs>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dateTarget} onClose={endFlow} fullWidth maxWidth="xs">
        {/* Tighter than MUI's dialog defaults: it's a one-line question + a date */}
        <DialogTitle sx={{ pb: 0.5 }}>Finished in {year}?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Standouts are things you finished this year. When did you finish{" "}
            {dateTarget?.label ? <b>{dateTarget.label}</b> : "it"}?
          </Typography>
          <DateSelectionButton
            value={finishDate}
            setValue={(value) =>
              setFinishDate(value ? value.format("YYYY-MM-DD") : null)
            }
            open={datePickerOpen}
            setOpen={setDatePickerOpen}
            minDate={year ? `${year}-01-01` : undefined}
            allowClear={false}
            showExpandIcon
          />
        </DialogContent>
        <DialogActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
          <Button onClick={endFlow}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmDate}
            disabled={!finishDate || isAdding}
          >
            {isAdding ? <Loading inline size="18px" /> : "Add standout"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
